/**
 * Operations UI scope helpers derived from profile role tokens (roleType / roleName),
 * not numeric roleId. Backend remains authoritative for data and permissions; this is
 * for filter defaults, wizard UX, and layout — never as the sole security gate.
 */

import { useMemo } from 'react'
import { useAuth } from '../../auth'
import type { UserModel } from '../../auth/core/_models'

/** Normalize backend RoleType-style strings from /profile/me. */
export function operationsRoleBehaviorKey(user: Pick<UserModel, 'role'> | null | undefined): string {
  const r = user?.role
  if (!r) return ''
  const raw =
    (r as { roleType?: string; roleName?: string; name?: string; code?: string }).roleType ||
    (r as { roleName?: string }).roleName ||
    (r as { name?: string }).name ||
    (r as { code?: string }).code
  if (raw && String(raw).trim()) {
    return String(raw).trim().toUpperCase().replace(/[\s-]/g, '_')
  }
  return legacyRoleIdFallback(r.id)
}

/** Last-resort mapping when API omits roleType (should be rare). */
function legacyRoleIdFallback(roleId: number | undefined): string {
  if (roleId == null) return ''
  switch (roleId) {
    case 1:
      return 'SUPER_ADMIN'
    case 2:
      return 'COMPANY_ADMIN'
    case 4:
      return 'CREW'
    case 5:
      return 'COMPANY_GROUP_ADMIN'
    case 6:
      return 'OPERATOR'
    default:
      return ''
  }
}

export type OperationsScopeUx = {
  behavior: string
  isCrew: boolean
  isOperator: boolean
  isCompanyAdmin: boolean
  isCompanyGroupAdmin: boolean
  /** Superadmin operator: OPERATOR with no company-group binding (wide list UX). */
  isSuperadminStyleOperator: boolean
  /** Operator tied to a company group (CGA-style UX). */
  isCompanyGroupTiedOperator: boolean
  actsAsSuperadmin: boolean
  actsAsCompanyGroupAdmin: boolean
  companyGroupAdminId: number | null
}

export function computeOperationsScopeUx(
  user: UserModel | null | undefined,
  isSuperAdminFromAccess: boolean
): OperationsScopeUx {
  const u = user as UserModel | undefined
  const companyGroupAdminId =
    u?.companyGroupAdminId ??
    (u?.companyGroupAdmin as { id?: number } | null | undefined)?.id ??
    null

  const behavior = operationsRoleBehaviorKey(u ?? null)
  const isCrew = behavior === 'CREW'
  const isOperator = behavior === 'OPERATOR'
  const isCompanyAdmin = behavior === 'COMPANY_ADMIN'
  const isCompanyGroupAdmin = behavior === 'COMPANY_GROUP_ADMIN'
  const isSuperadminStyleOperator = isOperator && companyGroupAdminId == null
  const isCompanyGroupTiedOperator = isOperator && companyGroupAdminId != null

  const actsAsSuperadmin = isSuperAdminFromAccess || isSuperadminStyleOperator
  const actsAsCompanyGroupAdmin = isCompanyGroupAdmin || isCompanyGroupTiedOperator

  return {
    behavior,
    isCrew,
    isOperator,
    isCompanyAdmin,
    isCompanyGroupAdmin,
    isSuperadminStyleOperator,
    isCompanyGroupTiedOperator,
    actsAsSuperadmin,
    actsAsCompanyGroupAdmin,
    companyGroupAdminId,
  }
}

export function useOperationsScopeUx(isSuperAdminFromAccess: boolean) {
  const { currentUser } = useAuth()
  return useMemo(
    () => computeOperationsScopeUx(currentUser as UserModel | undefined, isSuperAdminFromAccess),
    [currentUser, isSuperAdminFromAccess]
  )
}
